import { useCallback } from "react";
import { useImmer } from "use-immer";
import { v4 } from "uuid";

import type { AiNodeContext } from "@intelligems/ig-types/schemas/ai/context-schemas";
import type { StartWorkflowResponseBodyDto } from "@intelligems/ig-types/dtos/ai/workflows/start-workflow";
import { aiApiClient } from "@src/api/ai-api";

interface SyncWorkflowParams {
  workflowId: string;
  sessionId?: string;
  initialContext: Partial<AiNodeContext>;
  version?: string;
}

type SyncWorkflowResponse<TVariables extends object> =
  StartWorkflowResponseBodyDto<TVariables>;

interface SyncWorkflowState<TVariables extends object> {
  isPending: boolean;
  response: SyncWorkflowResponse<TVariables> | null;
  error: string | null;
}

export const useSyncWorkflow = <
  TVariables extends object = Record<string, unknown>,
>() => {
  const [state, setState] = useImmer<SyncWorkflowState<TVariables>>({
    isPending: false,
    response: null,
    error: null,
  });

  const mutate = useCallback(
    async ({
      workflowId,
      sessionId,
      initialContext,
      version,
    }: SyncWorkflowParams) => {
      setState((draft) => {
        draft.isPending = true;
        draft.response = null;
        draft.error = null;
      });

      try {
        const res = await aiApiClient.post<SyncWorkflowResponse<TVariables>>(
          "/workflows/execution/start-sync",
          {
            workflowId,
            sessionId: sessionId ?? v4(),
            initialContext,
            version,
          }
        );
        setState({
          isPending: false,
          response: res.data,
          error: null,
        });
        return res.data;
      } catch (err) {
        setState((draft) => {
          draft.isPending = false;
          draft.error =
            err instanceof Error ? err.message : "Sync execution failed";
        });
        return null;
      }
    },
    [setState]
  );

  const reset = useCallback(() => {
    setState((draft) => {
      draft.isPending = false;
      draft.response = null;
      draft.error = null;
    });
  }, [setState]);

  return { ...state, mutate, reset };
};
